/**
 * IGNIERE runtime layer — Horizon コア非編集で「§80〜§118」相当の機能を提供する。
 *
 * 設計原則（HANDOFF §3・Shopify Growth Services 準拠）：
 * - Horizon core snippet/block/section は一切編集しない。
 * - 本ファイルと snippets/igniere-brand.liquid（+ 追加 asset）のみで実現する。
 * - Web Component の morphSection 再描画に対応：MutationObserver で再適用。
 *
 * 実装対象（順次）：
 *   §118: blog 日付 YYYY.MM.DD 統一
 *   §83a: カードリンクから ?variant= 除去
 *   §99 : 色/サイズチップ内サブ行（残りわずか／欠品中）
 *   §81/§103/§104: 色選択で PDP ギャラリー画像を alt タグでフィルタ
 *   §83b: カード下カラーチップ（既存 snippet 相当を JS 生成）
 *   §80 : 色オプションを画像スウォッチ＋色名で描画
 */
(function () {
  'use strict';

  const IG = (window.IGNIERE_RUNTIME = window.IGNIERE_RUNTIME || {});
  IG.modules = IG.modules || {};

  /**
   * モジュール登録：apply(root) を実装。root は「今回走査すべき DOM ルート」。
   * 初回起動時は document・morph 後は差し替えられたノード。
   */
  IG.register = function (name, mod) {
    IG.modules[name] = mod;
    IG.debug && console.log('[IGNIERE] registered', name);
  };

  /**
   * 全モジュールを与えられた root に対して適用。
   * 例外が出ても他モジュールの処理を止めない。
   */
  IG.applyAll = function (root) {
    root = root || document;
    for (const [name, mod] of Object.entries(IG.modules)) {
      if (typeof mod.apply !== 'function') continue;
      try {
        mod.apply(root);
      } catch (err) {
        console.error('[IGNIERE][' + name + '] apply failed:', err);
      }
    }
  };

  /**
   * 起動：DOMContentLoaded で初回適用 + MutationObserver で差し替え検知。
   * morphSection・fetch 更新・variant change 後の DOM 差し替えに追随する。
   */
  function start() {
    IG.applyAll(document);

    if (IG.observer) return;
    // Horizon の Section Rendering API による DOM 差し替えは
    // 色変更の 1〜1.5 秒後に非同期で起きる。差し替え後、こちらが挿入した
    // ig-chip-sub / ig-swatch-name とマーカー data-ig99 / data-ig80b は
    // まとめて消える。addedNode subtree だけ apply しても遠くの label は届かない。
    // よって「変異があったら少し debounce して document 全体を apply」にする。
    function applyNow() {
      IG._applying = true;
      try {
        IG.applyAll(document);
      } finally {
        setTimeout(function () { IG._applying = false; }, 50);
      }
    }
    // §318（2026-10-13 代表の報告「サイズを選択した1〜2秒後に…『欠品中』の文字が一瞬消えて再出現」）：
    // 選ぶ部品（variant-picker）が描き直された時に 100ms 待ってから付け直すと、その間に「欠品中」や色名の無い画面が描かれる。
    // 変異の通知は描画の前に届くので、選ぶ部品の中の変異だけはその場で付け直す（ほかは従来どおりまとめて待つ）。
    function touchesPicker(ms) {
      for (const m of ms) {
        const t = m.target;
        if (t && t.nodeType === 1 && t.closest && t.closest('variant-picker')) return true;
        for (const n of m.addedNodes) {
          if (n.nodeType === 1 && (n.matches('variant-picker') || (n.querySelector && n.querySelector('variant-picker')))) return true;
        }
      }
      return false;
    }
    IG.observer = new MutationObserver((ms) => {
      if (IG._applying) return; // 自身の変異による無限ループ防止
      if (touchesPicker(ms)) {
        if (IG._reapplyTimer) { clearTimeout(IG._reapplyTimer); IG._reapplyTimer = null; }
        applyNow();
        return;
      }
      if (IG._reapplyTimer) clearTimeout(IG._reapplyTimer);
      IG._reapplyTimer = setTimeout(function () {
        IG._reapplyTimer = null;
        IG._applying = true;
        try {
          IG.applyAll(document);
        } finally {
          // 少し余韻を持たせて自身の変異が settle するまで re-entrant を防ぐ
          setTimeout(function () { IG._applying = false; }, 50);
        }
      }, 100);
    });
    IG.observer.observe(document.body, { childList: true, subtree: true });
  }

  // defer 属性の script でも、全モジュールの登録が終わってから apply したい。
  // DOMContentLoaded は defer script 群の実行完了後に発火するので、そこを起点にする。
  // (readyState が 'complete' で登録された遅参加モジュールは、start() 内で自動的に走らせる)
  if (document.readyState === 'complete') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
